import { type ReactElement, useEffect, useRef, useState } from 'react';
import { type RideSupportSummary } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { Badge, Button, ChipFrame, ChipMain, EmptyResult, EmptyState, IconButton, ListTable, Notice, Panel, Row, Text } from '@vello/web-theme';
import { classifySupportQuery, formatMarketInstant, marketDisplayName, marketTimeZone, parsePersonRidesFilter, personName, personRidesQuery, rideStatusTone, shortId, supportQueryNeedsMarket } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { LoadingBlock } from './loading-block';
import { usePersonName } from './person-name';

/** A search reads one page of at most this many rides (the server's page bound); a person's list continues with "Mostrar mais". */
const SEARCH_LIMIT = 50;

/**
 * The support lookup of the **Grade de corridas** (the old "Suporte a corridas" screen, merged into the grid — owner, 2026-10-18).
 * It answers `POST /v1/ops/ride-support/rides/search` (D413: the query travels in the request body, never in a URL; a phone search spends
 * the operator's hourly lookup budget) for one of two sources and replaces the grid's table while it is active:
 *
 * - **a typed lookup** (`lookupQ`): a ride id, an account id or an E.164 phone typed in the grid's search box. Only a ride id finds its own
 *   market; every other search runs inside the header scope's market.
 * - **a person filter in the URL** (`?person=<account id>&role=RIDER|DRIVER`, D580 follow-up): a person's record opens the grid with it,
 *   and the list is that person's rides — the ones they TOOK (rider) or DROVE (driver) — in the scope's market. It is a removable chip;
 *   removing it clears the URL query.
 *
 * Results only ever include rides the operator's `ride:view` grant reaches. Times render in each ride's market timezone and money from
 * integer minor units. A row opens the ride page.
 */
export function RideSupportLookup({ lookupQ, onClearLookup, market }: { lookupQ: string | undefined; onClearLookup: () => void; market: string }): ReactElement {
  const { runtime, markets, scope } = useAdmin();
  const { navigate, query: urlQuery, replaceQuery } = useRouter();
  const { t, lang } = useT();
  const { v } = useVocab();
  const [rides, setRides] = useState<readonly RideSupportSummary[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  // Bumped whenever the list restarts (filter or market changed, removed): an answer to an older page is dropped.
  const listSeq = useRef(0);
  const [personLabel, setPersonLabel] = useState<string | undefined>(undefined);

  // A typed lookup replaces the person filter (the grid clears the URL query when it starts one).
  const filter = lookupQ === undefined ? parsePersonRidesFilter(urlQuery) : undefined;
  const filterId = filter?.personId;
  const filterRole = filter?.role;

  // The chip names the person: the directory name (or the phone, in the scope country's pattern) once read; a short id before that.
  useEffect(() => {
    setPersonLabel(undefined);
    if (filterId === undefined) return;
    let cancelled = false;
    void runtime.adminUsers.get(filterId).then((res) => {
      if (!cancelled && res.ok) setPersonLabel(personName(res.value.user, scope.current.country));
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, filterId, scope]);

  // The person's rides load on their own (the filter IS the search) and again when the scope market changes.
  useEffect(() => {
    if (filterId === undefined || filterRole === undefined) return;
    const personQuery = personRidesQuery(filterId, market === '' ? undefined : market);
    if (personQuery === undefined) {
      setRides(undefined);
      setError(undefined);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadingMore(false);
    setError(undefined);
    setNextCursor(undefined);
    void runtime.rideSupport.search(personQuery.q, { market: personQuery.market, limit: SEARCH_LIMIT, role: filterRole }).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.ok) {
        setRides(res.value.rides);
        setNextCursor(res.value.nextCursor);
      } else {
        setRides(undefined);
        setError(res.error.code === 'RATE_LIMITED' ? t('desk.new.rateLimited') : t('rides.error', { code: res.error.code }));
      }
    });
    return () => {
      cancelled = true;
      listSeq.current += 1;
    };
  }, [runtime, filterId, filterRole, market, t]);

  // The typed lookup runs when it is submitted (and again when the scope market changes): an id, a code or a phone with +country code.
  useEffect(() => {
    if (lookupQ === undefined) return;
    const kind = classifySupportQuery(lookupQ);
    if ((supportQueryNeedsMarket(kind) && market === '') || kind === 'empty') {
      setRides(undefined);
      setError(undefined);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    setNextCursor(undefined);
    void runtime.rideSupport.search(lookupQ.trim(), { ...(market !== '' ? { market } : {}), limit: SEARCH_LIMIT }).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.ok) setRides(res.value.rides);
      else {
        setRides(undefined);
        // D413: a phone search spends the hourly lookup budget shared with the desk; beyond it the server refuses.
        setError(res.error.code === 'RATE_LIMITED' ? t('desk.new.rateLimited') : t('rides.error', { code: res.error.code }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, lookupQ, market, t]);

  // "Mostrar mais": the next page of the person's rides, appended (a ride is never listed twice).
  const showMore = async (): Promise<void> => {
    if (filterId === undefined || filterRole === undefined || nextCursor === undefined || loadingMore) return;
    const personQuery = personRidesQuery(filterId, market === '' ? undefined : market);
    if (personQuery === undefined) return;
    const seq = listSeq.current;
    setLoadingMore(true);
    const res = await runtime.rideSupport.search(personQuery.q, { market: personQuery.market, limit: SEARCH_LIMIT, role: filterRole, cursor: nextCursor });
    if (seq !== listSeq.current) return;
    setLoadingMore(false);
    if (res.ok) {
      setRides((prev) => {
        const seen = new Set((prev ?? []).map((r) => r.rideId));
        return [...(prev ?? []), ...res.value.rides.filter((r) => !seen.has(r.rideId))];
      });
      setNextCursor(res.value.nextCursor);
    } else {
      setError(res.error.code === 'RATE_LIMITED' ? t('desk.new.rateLimited') : t('rides.error', { code: res.error.code }));
    }
  };

  const removeFilter = (): void => {
    listSeq.current += 1;
    setRides(undefined);
    setNextCursor(undefined);
    setError(undefined);
    replaceQuery({});
  };

  const fare = (r: RideSupportSummary): string =>
    /^\d+$/.test(r.fare.amountMinor) && isIso4217Currency(r.fare.currency) ? formatMoney({ amountMinor: BigInt(r.fare.amountMinor), currency: r.fare.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) : '—';

  const filterPersonName = usePersonName(filter?.personId);
  const personChip = filter === undefined ? undefined : t(filter.role === 'DRIVER' ? 'rides.filter.driver' : 'rides.filter.rider', { name: personLabel ?? filterPersonName ?? shortId(filter.personId) });
  const lookupChip = lookupQ === undefined ? undefined : t('rides.lookup.chip', { q: lookupQ.trim() });

  return (
    <>
      <Panel>
        <Row as="ul" gap={2} align="stretch" wrap aria-label={t('rides.filter.group')}>
          {filter !== undefined && personChip !== undefined && (
            <ChipFrame>
              <ChipMain
                data-testid="rides-person-filter"
                title={t('rides.filter.open')}
                onClick={() => navigate(filter.role === 'DRIVER' ? 'driver_detail' : 'passenger_detail', { id: filter.personId })}
              >
                {personChip}
              </ChipMain>
              <IconButton icon="x" label={t('rides.filter.remove', { filter: personChip })} onClick={removeFilter} />
            </ChipFrame>
          )}
          {lookupChip !== undefined && (
            <ChipFrame>
              <ChipMain data-testid="rides-lookup-chip">{lookupChip}</ChipMain>
              <IconButton icon="x" label={t('rides.filter.remove', { filter: lookupChip })} onClick={onClearLookup} />
            </ChipFrame>
          )}
        </Row>
      </Panel>

      {error !== undefined && <Notice tone="danger">{error}</Notice>}

      {rides === undefined ? (
        <Panel>
          {loading ? <LoadingBlock /> : <EmptyState title={t('rides.search.hint.market')} />}
        </Panel>
      ) : (
        <ListTable
          columns={[
            { key: 'code', header: t('rides.col.code'), width: '1fr', render: (r: RideSupportSummary) => <Text mono>{r.code}</Text> },
            { key: 'status', header: t('dash.col.status'), width: '1.2fr', render: (r: RideSupportSummary) => <Badge tone={rideStatusTone(r.status)}>{v('ride.status', r.status)}</Badge> },
            { key: 'market', header: t('ride.field.market'), width: '1.2fr', render: (r: RideSupportSummary) => marketDisplayName(r.marketId, markets) },
            { key: 'requested', header: t('ride.col.requestedAt'), width: '1.3fr', render: (r: RideSupportSummary) => <span className="tnum">{formatMarketInstant(r.requestedAt, marketTimeZone(r.marketId, markets), lang)}</span> },
            { key: 'category', header: t('ride.field.category'), width: '.8fr', render: (r: RideSupportSummary) => v('ride.category', r.categoryCode) },
            { key: 'fare', header: t('rides.col.fare'), numeric: true, render: fare },
          ]}
          rows={rides}
          rowKey={(r) => r.rideId}
          onRowClick={(r) => navigate('ride_detail', { id: r.rideId })}
          empty={<EmptyResult title={t(filter !== undefined ? 'rides.filter.empty' : 'rides.search.empty')} />}
        />
      )}
      {filter !== undefined && nextCursor !== undefined && (
        <Row justify="center">
          <Button variant="secondary" onClick={() => void showMore()} disabled={loadingMore}>{t('rides.more')}</Button>
        </Row>
      )}
    </>
  );
}
